/* eslint-disable no-debugger */
import { FC, useEffect, useMemo } from 'react';
import Container from '@mui/material/Container';
import PageHeader from '@components/pageHeader';
import PortfolioSelection from '@components/portfolioSelection';
import { AutoGraph } from '@mui/icons-material';
import { Box, Divider, Grid, Typography } from '@mui/material';
import PortfolioValue from '@components/portfolioValue';
import { useAppDispatch, useAppSelector } from '@src/app/hooks/store';
import { getDashboardAsync } from '@src/app/store/reducers/dashboard/thunks';
import { useAppContext } from '@src/app/hooks/useAppContext';
import { useWindow } from '@src/app/hooks/useWindow';
import { DashboardChartData, DashboardChart } from './DashboardChart';
import { ScreenState } from '@src/app/enums';
import UnableToLoadData from '@components/unableToLoadData';
import { PortfolioInfoDetail } from 'clientportalshared';
import PortfolioCard from './PortfolioCard';
import { selectDashboard } from '@src/app/store/reducers/dashboard/selectors';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { DashboardAlert } from './DashboardAlert';
import { PageContent } from '@components/pageContent';
import CustomSkeleton from '@components/customSkeleton';

const Home: FC = (): JSX.Element => {
  const { portfoliosSelected, portfoliosIdsSelected, portfoliosCombined, portfolioInfoState } = useAppSelector(selectPortfolios);
  const { data, state } = useAppSelector(selectDashboard);
  const { transitioningMenu } = useAppContext();
  const { resizing } = useWindow();

  const dispatch = useAppDispatch();

  const fetchDashboardAsync = async () => {
    await dispatch(
      getDashboardAsync({
        includeValueOverTime: true,
        clientPortfolios: portfoliosSelected,
      })
    );
  };

  const charts: DashboardChartData[] = useMemo(() => {
    const allPortfolioDetails = data?.portfolios?.portfolioInfoDetails ?? [];
    const elements: DashboardChartData[] = allPortfolioDetails
      .filter((detail) => {
        const isSelected = portfoliosIdsSelected.includes(detail?.id ?? -1);
        const isCombined = portfoliosCombined && portfoliosIdsSelected.length > 1 && detail.id === 0 && detail.name === 'Combined';
        return isSelected || isCombined;
      })
      .map((detail: PortfolioInfoDetail) => ({
        valueOverTime: detail.valueOverTime,
        name: detail.name ?? '',
        id: detail.id ?? -1,
      }));
    return elements;
  }, [data, portfoliosSelected, portfoliosCombined]);

  useEffect(() => {
    if (state !== ScreenState.SuccessDataLoaded) {
      fetchDashboardAsync();
    }
  }, []);

  useEffect(() => {
    if (state !== ScreenState.Loading) {
      fetchDashboardAsync();
    }
  }, [portfoliosSelected]);

  if (state === ScreenState.ErrorUnableToLoadData || portfolioInfoState === ScreenState.ErrorUnableToLoadData) {
    return <UnableToLoadData />;
  }
  const isLoading = state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading;
  return (
    <Container maxWidth={false}>
      <Box display="flex" flexDirection="column" gap="20px" mb={6}>
        <DashboardAlert />
        <PageHeader title="Dashboard" icon={AutoGraph}>
          <PortfolioSelection allowCombining={true} />
        </PageHeader>

        <PageContent isLoading={isLoading}>
          <PortfolioValue totalValue={data?.portfolios?.totalValue ?? 0} />
          <Divider />
          <Typography fontWeight={500} fontSize="16px" color="#222222">
            Value Over Time
          </Typography>

          {transitioningMenu || resizing ? <CustomSkeleton variant="rounded" width="100%" height="300px" /> : <DashboardChart charts={charts} />}
        </PageContent>

        {data && data?.portfolios?.portfolioInfoDetails && (
          <Grid container spacing={2}>
            {data.portfolios.portfolioInfoDetails
              .filter((portfolio) => portfolio.id && portfoliosIdsSelected.includes(portfolio.id || -1))
              .map((portfolio, index) => (
                <PortfolioCard key={index} {...portfolio} />
              ))}
          </Grid>
        )}
      </Box>
    </Container>
  );
};

export default Home;
